import React, { useMemo } from 'react'
import {
  type FieldErrors,
  type FieldValues,
  FormProvider,
} from 'react-hook-form'
import {
  type FormCol,
  type FormLayout,
  FormLayoutContext,
} from 'sutra/components/Form/form-layout'
import { StyledForm } from 'sutra/components/Form/form-styles'
import type { FormInstance } from 'sutra/components/Form/use-form'

export interface FormProps<
  TValues extends FieldValues = FieldValues,
> extends Omit<
  React.FormHTMLAttributes<HTMLFormElement>,
  'onSubmit' | 'onReset'
> {
  // From `useForm`. Required for any `FormItem` with a `name`; without it the
  // form only lays out its items and submits through `onFinish`.
  form?: FormInstance<TValues>
  // Runs on submit once every rule passes. With no `form` it receives `{}`.
  onFinish?: (values: TValues) => void | Promise<void>
  onFinishFailed?: (errors: FieldErrors<TValues>) => void
  layout?: FormLayout
  labelCol?: FormCol
  wrapperCol?: FormCol
  // Whether a required item shows its asterisk.
  requiredMark?: boolean
  children?: React.ReactNode
}

export function Form<TValues extends FieldValues = FieldValues>({
  form,
  onFinish,
  onFinishFailed,
  layout = 'vertical',
  labelCol,
  wrapperCol,
  requiredMark = true,
  children,
  ...rest
}: FormProps<TValues>): React.JSX.Element {
  const layoutValue = useMemo(
    () => ({ layout, labelCol, wrapperCol, requiredMark }),
    [layout, labelCol, wrapperCol, requiredMark],
  )

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    if (!form) {
      event.preventDefault()
      void onFinish?.({} as TValues)
      return
    }
    void form.handleSubmit(
      (values) => onFinish?.(values),
      onFinishFailed,
    )(event)
  }

  const body = (
    <FormLayoutContext.Provider value={layoutValue}>
      <StyledForm {...rest} $layout={layout} noValidate onSubmit={handleSubmit}>
        {children}
      </StyledForm>
    </FormLayoutContext.Provider>
  )
  return form ? <FormProvider {...form}>{body}</FormProvider> : body
}
